// @catalog F.047
// Bloque "Resumen mensual" de /mercado-libre (CR-4/CR-5): economía real del
// canal por mes — bruto, comisión pura, retenciones 2026, envío neto,
// reembolsos, neto, costo Odoo y ambos márgenes (spec restricción 8), con la
// tasa de match que contextualiza el costo.
import { Box, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  BodyCell,
  BodyRow,
  HeaderCell,
  TableShell,
} from "../analytics/TableCells";
import MercadoLibreImportService, {
  type MonthlySummaryRow,
} from "../../services/mercadoLibreImport.service";

const fmt = MercadoLibreImportService.formatCurrency;
const fmtPct = (v: number): string => `${Math.round(v * 100)}%`;

export function ResumenMensualBlock(): JSX.Element {
  const summaryQuery = useQuery({
    queryKey: ["ml-summary-monthly"],
    queryFn: () => MercadoLibreImportService.getMonthlySummary(),
  });
  const rows: MonthlySummaryRow[] = summaryQuery.data ?? [];

  return (
    <Box>
      <Typography
        component="h2"
        sx={{ fontSize: "16px", fontWeight: 600, color: "var(--ink-1)", mb: 1.5 }}
      >
        Resumen mensual
      </Typography>
      <TableShell maxHeight="max(360px, calc(100vh - 300px))">
        <Box component="thead">
          <Box component="tr">
            <HeaderCell>Mes</HeaderCell>
            <HeaderCell align="right">Pedidos</HeaderCell>
            <HeaderCell align="right">Bruto</HeaderCell>
            <HeaderCell align="right">Comisión</HeaderCell>
            <HeaderCell align="right">Retenciones</HeaderCell>
            <HeaderCell align="right">Envío neto</HeaderCell>
            <HeaderCell align="right">Reembolsos</HeaderCell>
            <HeaderCell align="right">Neto</HeaderCell>
            <HeaderCell align="right">Costo Odoo</HeaderCell>
            <HeaderCell align="right">Margen real</HeaderCell>
            <HeaderCell align="right">Margen post-ret.</HeaderCell>
            <HeaderCell align="right">Match</HeaderCell>
          </Box>
        </Box>
        <Box component="tbody">
          {rows.length === 0 && (
            <Box component="tr">
              <BodyCell muted>
                {summaryQuery.isLoading ? "Cargando…" : "Sin datos importados."}
              </BodyCell>
            </Box>
          )}
          {rows.map((r) => (
            <BodyRow key={r.month}>
              <BodyCell>{r.month}</BodyCell>
              <BodyCell align="right">
                {r.ordersCount.toLocaleString()}
              </BodyCell>
              <BodyCell align="right">{fmt(r.gross)}</BodyCell>
              <BodyCell align="right">{fmt(r.commissionPure)}</BodyCell>
              <BodyCell align="right">
                <Tooltip
                  arrow
                  title={`IVA ${fmt(r.withholdingIva)} · ISR ${fmt(r.withholdingIsr)} (estimadas, acreditables — 2026)`}
                >
                  <span>{fmt(r.withholdingIva + r.withholdingIsr)}</span>
                </Tooltip>
              </BodyCell>
              <BodyCell align="right">{fmt(r.shipNet)}</BodyCell>
              <BodyCell align="right">{fmt(r.refunds)}</BodyCell>
              <BodyCell align="right">{fmt(r.totalReceived)}</BodyCell>
              <BodyCell align="right" muted={r.odooCostTotal === null}>
                {fmt(r.odooCostTotal)}
              </BodyCell>
              <BodyCell align="right">{fmt(r.marginReal)}</BodyCell>
              <BodyCell align="right">{fmt(r.marginPostWithholdings)}</BodyCell>
              <BodyCell align="right" muted>
                {fmtPct(r.matchRate)}
              </BodyCell>
            </BodyRow>
          ))}
        </Box>
      </TableShell>
    </Box>
  );
}
